import { initializeApp, FirebaseApp } from 'firebase/app';
import { 
  getAuth, 
  Auth,
  connectAuthEmulator,
  User
} from 'firebase/auth';
import { 
  getFirestore, 
  Firestore,
  connectFirestoreEmulator 
} from 'firebase/firestore';
import { 
  getStorage, 
  Storage,
  connectStorageEmulator 
} from 'firebase/storage';
import { 
  getMessaging, 
  Messaging 
} from 'firebase/messaging';

// Check if we should use emulator mode
// IMPORTANT: GitHub Actions should NEVER use emulator - always set VITE_USE_EMULATOR=false
const useEmulator = import.meta.env.VITE_USE_EMULATOR === 'true';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'demo-api-key',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'demo-project.firebaseapp.com',
  projectId: useEmulator ? 'work-order-managment' : (import.meta.env.VITE_FIREBASE_PROJECT_ID || 'work-order-managment'),
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'demo-project.appspot.com',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '123456789',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || 'demo-app-id',
};

export class FirebaseService {
  private app: FirebaseApp;
  public auth: Auth;
  public firestore: Firestore;
  public storage: Storage;
  public messaging: Messaging | null = null;
  private isInitialized = false;

  constructor() {
    this.app = initializeApp(firebaseConfig);
    this.auth = getAuth(this.app);
    this.firestore = getFirestore(this.app);
    this.storage = getStorage(this.app);
    
    // Initialize messaging (only if supported in browser)
    this.initializeMessaging();
    
    this.connectToEmulators();
  }

  private initializeMessaging() {
    // Check if we're in a browser environment and notifications are supported
    if (typeof window !== 'undefined' && 'Notification' in window) {
      try {
        this.messaging = getMessaging(this.app);
        console.log('[Firebase] Messaging initialized successfully');
      } catch (error) {
        console.warn('[Firebase] Messaging not supported or failed to initialize:', error);
        this.messaging = null;
      }
    } else {
      console.warn('[Firebase] Messaging not supported in this environment');
    }
  }

  private connectToEmulators() {
    if (useEmulator && !this.isInitialized) {
      // Safety check: Never use emulator in production builds
      if (import.meta.env.PROD) {
        console.warn('⚠️ WARNING: Attempting to use Firebase emulator in production build! This should never happen.');
        return;
      }
      
      try {
        // Connect to Firestore emulator
        connectFirestoreEmulator(this.firestore, 'localhost', 8080);
        
        // Connect to Auth emulator
        connectAuthEmulator(this.auth, 'http://localhost:9099', { disableWarnings: true });
        
        // Connect to Storage emulator
        connectStorageEmulator(this.storage, 'localhost', 9199);
        
        this.isInitialized = true;
        
      } catch (error) {
        // Emulators already connected, ignore error

        this.isInitialized = true;
      }
    }
  }

  // Get the Firestore instance (this is what components will use)
  getFirestore(): Firestore {
    return this.firestore;
  }

  // Get the Auth instance
  getAuth(): Auth {
    return this.auth;
  }

  // Get the Storage instance
  getStorage(): Storage {
    return this.storage;
  }

  // Get the Messaging instance (may be null if not supported)
  getMessaging(): Messaging | null {
    return this.messaging;
  }

  // Check if messaging is supported
  isMessagingSupported(): boolean {
    return this.messaging !== null;
  }

  // Check if using emulator
  isUsingEmulator(): boolean {
    return useEmulator;
  }

  // Get current authenticated user
  getCurrentUser(): User | null {
    return this.auth.currentUser;
  }
}

// Create and export singleton instance
export const firebaseService = new FirebaseService();
